import * as Leaflet from 'leaflet'
import * as React from 'react'
import * as Rx from 'rxjs'
import { Map as MapComponent, TileLayer } from 'react-leaflet'

import MapObjectCRS from './MapObjects/MapObjectCRS'
import MapObjectLayer from './MapObjects/MapObjectLayer'
import WanderCRS from '../models/WanderCRS'
import { MapObjectEvent } from '../io/events'
import { Point, Rect, rectEquals } from '../models/BasicTypes'

export interface Props {
  /// Map tile URL
  tileURL: string
  /// Map attribution
  mapAttribution: string
  /// Display attribution
  displayAttribution: boolean
  /// Events to be processed
  events: Array<MapObjectEvent>
  /// Callback to be called when we processed all given events
  onProcessEvent?: (id: string) => void
  /// The ID of map object the camera should focus on
  focusMapObjectID?: string
  /// View port update period
  viewPortUpdatePeriod?: number
  /// Called when view port updated
  onViewPortUpdate?: (viewPort: Rect) => void
  /// Called when a destiation is update
  onMove?: (dest: Point, focusMapObjectPosition?: Point) => void
  /// Called when a map object press
  onMapObjectPress?: (id: string) => void
}

export default class World extends React.Component<Props> {
  private readonly events$ = new Rx.Subject<MapObjectEvent>()
  private readonly subscription = new Rx.Subscription()
  private readonly mapMouseDown$ = new Rx.Subject<Leaflet.LeafletMouseEvent>()
  private readonly mapMouseMove$ = new Rx.Subject<Leaflet.LeafletMouseEvent>()
  private readonly mapMouseUp$ = new Rx.Subject<Leaflet.LeafletMouseEvent>()
  private readonly mapMouseOut$ = new Rx.Subject<Leaflet.LeafletMouseEvent>()
  private readonly viewPortUpdatePeriod$ = new Rx.BehaviorSubject<
    number | undefined
  >(5 * 1000)
  private readonly mapUpdate$ = new Rx.Subject<void>()
  private mapObjectLayer: MapObjectLayer
  private mapComponent: MapComponent
  private wanderCRS: WanderCRS

  get leafletMap (): Leaflet.Map {
    return this.mapComponent.leafletElement
  }

  get mapObjectCRS (): MapObjectCRS {
    return this.mapObjectLayer.mapObjectCRS
  }

  constructor (props: Props, context?: any) {
    super(props, context)
    // XXX: should be passed in via context instead?
    this.wanderCRS = new WanderCRS()
  }

  componentDidMount () {
    this.onMapUpdate()
    this.leafletMap.on('zoomend viewreset', this.onMapUpdate, this)
    this.processEvents(this.props.events)
    this.viewPortUpdatePeriod$.next(this.props.viewPortUpdatePeriod)
  }

  componentWillUpdate (nextProps: Props) {
    if (this.props.viewPortUpdatePeriod !== nextProps.viewPortUpdatePeriod) {
      this.viewPortUpdatePeriod$.next(nextProps.viewPortUpdatePeriod)
    }
  }

  componentWillUnmount () {
    this.leafletMap.off('zoomend viewreset', this.onMapUpdate, this)
    this.subscription.unsubscribe()
  }

  shouldComponentUpdate (nextProps: Props) {
    if (this.props.events !== nextProps.events) {
      this.processEvents(nextProps.events)
    }
    // Notice: we don't want to update component for events change
    return (
      this.props.tileURL !== nextProps.tileURL ||
      this.props.mapAttribution !== nextProps.mapAttribution ||
      this.props.onProcessEvent !== nextProps.onProcessEvent ||
      this.props.focusMapObjectID !== nextProps.focusMapObjectID ||
      this.props.viewPortUpdatePeriod !== nextProps.viewPortUpdatePeriod ||
      this.props.onViewPortUpdate !== nextProps.onViewPortUpdate ||
      this.props.onMove !== nextProps.onMove ||
      this.props.onMapObjectPress !== nextProps.onMapObjectPress
    )
  }

  render () {
    // XXX:
    const position = { lng: -122.409745, lat: 37.782817 }
    return (
      <div className='map-container'>
        <MapComponent
          center={position}
          zoom={15}
          onmousedown={this.onMapMouseDown}
          onmousemove={this.onMapMouseMove}
          onmouseup={this.onMapMouseUp}
          onmouseout={this.onMapMouseOut}
          zoomControl={false}
          ref={this.onMapComponentRef}
          attributionControl={this.props.displayAttribution}>
          <TileLayer
            url={this.props.tileURL}
            attribution={this.props.mapAttribution}
          />
          <MapObjectLayer
            wanderCRS={this.wanderCRS}
            events$={this.events$}
            focusMapObjectID={this.props.focusMapObjectID}
            onMapObjectPress={this.props.onMapObjectPress}
            ref={this.onMapObjectLayerRef}
          />
        </MapComponent>
      </div>
    )
  }

  private onMapObjectLayerReady () {
    // Update view port periodically
    this.subscription.add(
      this.viewPortUpdatePeriod$
        .switchMap(
          period =>
            period
              ? Rx.Observable.merge(
                  Rx.Observable.interval(period),
                  this.mapUpdate$
                )
              : Rx.Observable.empty()
        )
        .startWith(0)
        .map(() => {
          const bounds = this.leafletMap!.getBounds()
          const leftTop = this.leafletMap!.latLngToLayerPoint(
            bounds.getNorthWest()
          )
          const bottomRight = this.leafletMap!.latLngToLayerPoint(
            bounds.getSouthEast()
          )
          const { mapObjectCRS } = this
          const wanderLeftTop = mapObjectCRS.relativeToWander(leftTop)
          const wanderBottomRight = mapObjectCRS.relativeToWander(bottomRight)
          return {
            origin: wanderLeftTop,
            size: {
              width: wanderBottomRight.x - wanderLeftTop.x,
              height: wanderBottomRight.y - wanderLeftTop.y
            }
          }
        })
        .distinctUntilChanged(rectEquals)
        .subscribe(viewPort => {
          if (this.props.onViewPortUpdate) {
            this.props.onViewPortUpdate(viewPort)
          }
        })
    )

    // collect mouse events for updating player destination
    this.subscription.add(
      this.mapMouseDown$
        // Notice: it's possible our mouse down tapped on image view or other
        // stuff, to avoid move for that kind of situation, we only start
        // moving if the target has no ignore-tap class in its ancestors
        .filter(
          down => !this.ignoreTaps(down.originalEvent.target as HTMLElement)
        )
        .map(down =>
          Rx.Observable
            .merge(this.mapMouseUp$, this.mapMouseOut$)
            .take(1)
            .multicast(
              () => new Rx.ReplaySubject<Leaflet.LeafletMouseEvent>(1),
              stop =>
                // take move events until stop, but also concat the stop event
                // as a tail
                Rx.Observable
                  .interval(100)
                  .withLatestFrom(this.mapMouseMove$, (_, v) => v)
                  .takeUntil(stop)
                  .concat(stop)
                  .startWith(down)
            )
            .map(event => {
              const p = event.containerPoint
              const map = this.leafletMap!
              const layerPoint = map.containerPointToLayerPoint([p.x, p.y])
              const wanderPoint = this.mapObjectCRS.relativeToWander(layerPoint)
              return wanderPoint
            })
            .multicast(
              () => new Rx.ReplaySubject<Point>(1),
              o => o.throttleTime(1 * 1000).concat(o.take(1))
            )
        )
        .concatAll()
        .filter(() => !!this.props.focusMapObjectID)
        .subscribe(dest => {
          if (this.props.onMove) {
            const { focusMapObjectID } = this.props
            let focusMapObjectPosition: Point | undefined = undefined
            if (focusMapObjectID) {
              const positionInfo = this.mapObjectLayer.getMapObjectPosition(
                focusMapObjectID
              )
              if (positionInfo) {
                focusMapObjectPosition = this.mapObjectCRS.relativeToWander(
                  positionInfo.base
                )
              }
            }
            this.props.onMove(dest, focusMapObjectPosition)
          }
        })
    )
  }

  // Should we ignore tap on a given element?
  private ignoreTaps (element: HTMLElement): boolean {
    let currentElement: HTMLElement | null = element
    while (currentElement) {
      if (currentElement.classList.contains('ignore-taps')) {
        return true
      }
      currentElement = currentElement.parentElement
    }
    return false
  }

  private processEvents (events: Array<MapObjectEvent>) {
    let lastID: string | null = null
    for (const event of events) {
      this.events$.next(event)
      lastID = event.id
    }
    if (lastID && this.props.onProcessEvent) {
      this.props.onProcessEvent(lastID)
    }
  }

  private onMapComponentRef = (mapComponent: MapComponent) => {
    this.mapComponent = mapComponent
  }

  private onMapMouseMove = (event: Leaflet.LeafletMouseEvent) => {
    this.mapMouseMove$.next(event)
  }

  private onMapMouseDown = (event: Leaflet.LeafletMouseEvent) => {
    this.mapMouseDown$.next(event)
  }

  private onMapMouseUp = (event: Leaflet.LeafletMouseEvent) => {
    this.mapMouseUp$.next(event)
  }

  private onMapMouseOut = (event: Leaflet.LeafletMouseEvent) => {
    this.mapMouseOut$.next(event)
  }

  private onMapObjectLayerRef = (mapObjectLayer: MapObjectLayer) => {
    this.mapObjectLayer = mapObjectLayer
    this.onMapObjectLayerReady()
  }

  private onMapUpdate = () => {
    this.mapUpdate$.next()
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/World.tsx